import { useState } from 'react';
import { Dropdown, type MenuProps } from 'antd';
import DsIcon from './DsIcon';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/store/auth.store';
import { useDeploymentStore } from '@/store/deployment.store';
import { useAccountProfile } from './hooks/useAccountProfile';
import { copyToClipboard } from '@/utils/utils';
import type { DeploymentLogoUrl } from '@/config/deployment/deployment.types';

interface TopNavProps {
  collapsed: boolean;
  onLogout: () => void;
  onEnterAccessControl: () => void;
}

function resolveConfiguredLogo(
  logoUrl: DeploymentLogoUrl | undefined,
  theme: 'light' | 'dark',
  collapsed: boolean
): string | undefined {
  if (typeof logoUrl === 'string') return logoUrl;
  if (!logoUrl) return undefined;
  if (collapsed && logoUrl.collapsed) return logoUrl.collapsed;
  return theme === 'dark' ? logoUrl.dark : logoUrl.light;
}

export default function TopNav({ collapsed, onLogout, onEnterAccessControl }: TopNavProps) {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuthStore();
  const deploymentProfile = useDeploymentStore((state) => state.profile);
  const canViewProfile = useDeploymentStore((state) => state.hasCapability('profile.visible'));
  const { data: profile } = useAccountProfile();
  const [copiedField, setCopiedField] = useState<'id' | 'login' | 'domain' | null>(null);

  // 账号摘要优先使用接口返回的 profile；缺失字段对应展示直接隐藏。
  const account = profile?.account;
  const iamUser = profile?.user;
  const domain = profile?.domain;

  const displayName =
    iamUser?.display_name || iamUser?.name || account?.name || user?.username || '用户';
  const accountId = iamUser?.id || account?.id || user?.user_id || '';
  const loginName = iamUser?.name || account?.name || '';
  const email = iamUser?.email || account?.email || '';
  const phone = iamUser?.phone || account?.phone || '';
  const domainAlias = domain?.domain_alias ? `${loginName}@${domain.domain_alias}` : '';
  const logoUrl = resolveConfiguredLogo(
    deploymentProfile.branding.logoUrl,
    theme,
    collapsed
  );

  // const userType = user?.user_type;
  // const tagText = userType === 'admin' ? '管理员' : userType === 'user' ? '子账号' : '主账号';
  // const tagColor = userType === 'admin' ? 'gold' : userType === 'user' ? 'blue' : 'purple';

  const copyValue = async (value: string, field: 'id' | 'login' | 'domain') => {
    const ok = await copyToClipboard(value);
    if (!ok) return;
    setCopiedField(field);
    window.setTimeout(
      () => setCopiedField((current) => (current === field ? null : current)),
      1500
    );
  };

  const accountMenu: MenuProps = {
    items: [
      {
        type: 'group',
        label: (
          <div className="account-summary">
            <strong>
              <span className="account-summary-name">{displayName}</span>
              {/* <Tag color={tagColor}>{tagText}</Tag> */}
            </strong>
            {accountId && (
              <div className="account-summary-row">
                <span>{accountId}</span>
                <button
                  className="account-copy-btn"
                  type="button"
                  title={copiedField === 'id' ? '用户 ID 已复制' : '复制用户 ID'}
                  aria-label={copiedField === 'id' ? '用户 ID 已复制' : '复制用户 ID'}
                  onClick={(e) => {
                    e.stopPropagation();
                    void copyValue(accountId, 'id');
                  }}
                >
                  <DsIcon name={copiedField === 'id' ? 'check' : 'copy'} size={14} />
                </button>
              </div>
            )}
            {loginName && loginName !== displayName && (
              <div className="account-summary-row">
                <span>{loginName}</span>
                <button
                  className="account-copy-btn"
                  type="button"
                  title={copiedField === 'login' ? '登录名称已复制' : '复制登录名称'}
                  aria-label={copiedField === 'login' ? '登录名称已复制' : '复制登录名称'}
                  onClick={(e) => {
                    e.stopPropagation();
                    void copyValue(loginName, 'login');
                  }}
                >
                  <DsIcon name={copiedField === 'login' ? 'check' : 'copy'} size={14} />
                </button>
              </div>
            )}
            {email && <div className="account-summary-meta">{email}</div>}
            {phone && <div className="account-summary-meta">{phone}</div>}
            {domainAlias && (
              <div className="account-summary-row">
                <span>{domainAlias}</span>
                <button
                  className="account-copy-btn"
                  type="button"
                  title={copiedField === 'domain' ? '域别名已复制' : '复制域别名'}
                  aria-label={copiedField === 'domain' ? '域别名已复制' : '复制域别名'}
                  onClick={(e) => {
                    e.stopPropagation();
                    void copyValue(domainAlias, 'domain');
                  }}
                >
                  <DsIcon name={copiedField === 'domain' ? 'check' : 'copy'} size={14} />
                </button>
              </div>
            )}
          </div>
        ),
        children: [],
      },
      { type: 'divider' },
      ...(canViewProfile
        ? [
            {
              key: 'profile',
              label: '个人信息',
              icon: <DsIcon name="user" size={15} />,
              extra: <DsIcon name="external-link" size={14} />,
            },
          ]
        : []),
      { key: 'iam', label: '访问控制', icon: <DsIcon name="shield-check" size={15} /> },
      { type: 'divider' },
      { key: 'logout', label: '退出登录', icon: <DsIcon name="log-out" size={15} />, danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'profile') {
        window.open('https://cloud.infini-ai.com/account/user', '_blank', 'noopener,noreferrer');
      }
      if (key === 'iam') onEnterAccessControl();
      if (key === 'logout') onLogout();
    },
  };

  return (
    <header className="topnav">
      <div className="topnav-brandzone">
        {logoUrl && (
          <span className="brand">
            <img
              src={logoUrl}
              alt={deploymentProfile.branding.productName}
              className="brand-logo"
            />
          </span>
        )}
        <span className="product-title">一体化智能体运行与协同平台</span>
      </div>

      <div className="topnav-actions">
        <button
          className="icon-btn"
          title={theme === 'dark' ? '切到亮色' : '切到暗色'}
          type="button"
          onClick={toggleTheme}
        >
          <DsIcon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
        </button>
        <div className="top-sep" />
        <Dropdown menu={accountMenu} trigger={['click']} placement="bottomRight">
          <button className="avatar-z" title="账户" type="button" aria-label="打开账户菜单">
            <DsIcon name="user" size={16} />
          </button>
        </Dropdown>
      </div>
    </header>
  );
}
